/**
 * Recipient-side data + submit lib for input requests (PRD-005 / ARCH-028,
 * Batch IR-2a).
 *
 * The recipient reads their own open/answered/closed `input_requests` rows via
 * RLS `input_requests_recipient_select` (migration 0033: own non-deleted rows
 * via is_owner(subscriber_id)) and answers exactly once by calling the
 * SECURITY DEFINER RPC `submit_input_response` (migration 0035) as the
 * `authenticated` role. There is NO recipient table-write grant (ARCH-028
 * `no-recipient-update-grant-on-input-requests`): the RPC is the only write
 * path, and it re-validates server-side. These helpers therefore only ever
 * SELECT and call rpc().
 *
 * The three recipient surfaces (OS owner card, task card, general card) each
 * fetch the single relevant request with one of the scoped fetchers below; the
 * row carries everything the shared form needs (`questions`, `answers`,
 * `status`).
 */

import { supabase } from '@/lib/supabase';
import {
  inputRequestAnswersSchema,
  inputRequestQuestionsSchema,
  type InputRequestAnswers,
  type InputRequestContextType,
  type InputRequestQuestions,
} from '@studio/api-schemas';

/** Row status mirrored from the `input_requests.status` CHECK (migration 0033). */
export type InputRequestStatus = 'open' | 'answered' | 'closed';

/**
 * A relative "sent {when}" phrase from an ISO timestamp, in the recipient's
 * warm register: "just now" / "20 min ago" / "earlier today" / "yesterday" /
 * "on {date}". Deliberately coarse: the card is a calm affordance, not a log.
 * Lives in the lib (not chrome.tsx) so the chrome module stays component-only
 * for react-refresh.
 */
export function sentWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'recently';
  const min = Math.floor((Date.now() - then) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  if (then >= startOfToday.getTime()) return 'earlier today';
  if (then >= startOfToday.getTime() - 86400000) return 'yesterday';
  return `on ${new Date(then).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })}`;
}

/**
 * The recipient-readable projection of an `input_requests` row. Only the fields
 * the recipient surfaces compose. `questions`/`answers` are JSONB on the row;
 * we parse them through the shared schemas so the form receives typed data and
 * a malformed row degrades to "no card" rather than a render crash.
 */
export interface RecipientInputRequest {
  id: string;
  context_type: InputRequestContextType;
  title: string;
  intro: string | null;
  questions: InputRequestQuestions;
  answers: InputRequestAnswers;
  status: InputRequestStatus;
  answered_at: string | null;
  created_at: string;
}

const SELECT_COLUMNS =
  'id, context_type, title, intro, questions, answers, status, answered_at, created_at';

interface RawRow {
  id: string;
  context_type: string;
  title: string;
  intro: string | null;
  questions: unknown;
  answers: unknown;
  status: string;
  answered_at: string | null;
  created_at: string;
}

/**
 * Parse a raw row into a typed `RecipientInputRequest`, or null when the JSONB
 * payload does not validate against the shared schemas. Returning null lets the
 * surfaces degrade to "no card" honestly rather than rendering a broken form.
 */
function toTyped(row: RawRow): RecipientInputRequest | null {
  const questions = inputRequestQuestionsSchema.safeParse(row.questions);
  if (!questions.success) return null;
  const answers = inputRequestAnswersSchema.safeParse(row.answers ?? {});
  const status: InputRequestStatus =
    row.status === 'answered'
      ? 'answered'
      : row.status === 'closed'
        ? 'closed'
        : 'open';
  const context_type: InputRequestContextType =
    row.context_type === 'os'
      ? 'os'
      : row.context_type === 'task'
        ? 'task'
        : 'general';
  return {
    id: row.id,
    context_type,
    title: row.title,
    intro: row.intro,
    questions: questions.data,
    answers: answers.success ? answers.data : {},
    status,
    answered_at: row.answered_at,
    created_at: row.created_at,
  };
}

/**
 * The single most-recent non-closed request for one OS engagement (surface B).
 * RLS scopes to the caller's own rows, so no subscriber filter is passed; the
 * `os_engagement_id` filter targets the one engagement on screen. We surface a
 * closed request too (the card shows the closed-while-open state), so the only
 * rows excluded are soft-deleted ones (RLS already excludes those). Newest
 * first; one row.
 */
export async function fetchOsInputRequest(
  osEngagementId: string,
): Promise<RecipientInputRequest | null> {
  const { data, error } = await supabase
    .from('input_requests')
    .select(SELECT_COLUMNS)
    .eq('context_type', 'os')
    .eq('os_engagement_id', osEngagementId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data ? toTyped(data as RawRow) : null;
}

/** The single most-recent request for one task (surface C). */
export async function fetchTaskInputRequest(
  taskId: string,
): Promise<RecipientInputRequest | null> {
  const { data, error } = await supabase
    .from('input_requests')
    .select(SELECT_COLUMNS)
    .eq('context_type', 'task')
    .eq('task_id', taskId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data ? toTyped(data as RawRow) : null;
}

/**
 * The single most-recent `general` request for the caller (surface D). RLS
 * already scopes to the caller's own rows, so no subscriber filter is needed.
 */
export async function fetchGeneralInputRequest(): Promise<RecipientInputRequest | null> {
  const { data, error } = await supabase
    .from('input_requests')
    .select(SELECT_COLUMNS)
    .eq('context_type', 'general')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data ? toTyped(data as RawRow) : null;
}

/** The outcome of a submit attempt, surfaced to the form for messaging. */
export type SubmitResult =
  | { ok: true }
  | { ok: false; message: string };

/**
 * Submit the recipient's answers via the SECURITY DEFINER RPC (migration 0035).
 * The RPC is the only write path (no table grant); it re-validates answers
 * against the row's own questions server-side, sets status='answered', writes
 * answered_at, and fires the operator `input_submitted` notification. It returns
 * void; a Postgres-side validation failure surfaces as a thrown error here.
 */
export async function submitInputResponse(
  requestId: string,
  answers: InputRequestAnswers,
): Promise<SubmitResult> {
  const { error } = await supabase.rpc('submit_input_response', {
    p_request_id: requestId,
    p_answers: answers,
  });
  if (error) {
    return {
      ok: false,
      message:
        'Could not send your answers just now. Check the highlighted fields and try again.',
    };
  }
  return { ok: true };
}
